import type { ReactNode } from 'react';
import { Card, CardHeader, CardSection, CardTitle, Icon, Spinner, Stack } from '@customerio/pluma-components/react';
import { PushConfigErrors } from './push-config-errors';
import type { PushSettingsError } from '../utils/push-errors';

type AuthorizationStatusProps = {
  isBusy: boolean;
  hasErrors: boolean;
};

function AuthorizationStatus({ isBusy, hasErrors }: AuthorizationStatusProps) {
  if (isBusy) {
    return <Spinner />;
  }
  if (hasErrors) {
    return <Icon name="critical" color="critical" />;
  }
  return null;
}

export type PushConfigBodyProps = {
  errors: readonly PushSettingsError[];
  onDismissError: (error: PushSettingsError) => void;
  isBusy: boolean;
  chooser: ReactNode;
  fields: ReactNode;
  saveBar: ReactNode;
  sendTest: ReactNode;
};

export function PushConfigBody({
  errors,
  onDismissError,
  isBusy,
  chooser,
  fields,
  saveBar,
  sendTest,
}: PushConfigBodyProps) {
  return (
    <Stack as="section" gap="100">
      <PushConfigErrors errors={errors} onDismiss={onDismissError} />

      {/*
        Each card takes the width it needs rather than the column's: the
        authorization asks for the full width and says so, the test form is a
        compact card beside it. Stretching both would pull the recipient field
        across the page and push its button to the far edge.
      */}
      <Stack gap="100" alignInline="start">
        <Card w="full">
          <CardHeader columnGap="100">
            <CardTitle>Authorization</CardTitle>
            <AuthorizationStatus isBusy={isBusy} hasErrors={errors.length > 0} />
          </CardHeader>

          <CardSection>{chooser}</CardSection>
          <CardSection>{fields}</CardSection>
          <CardSection>{saveBar}</CardSection>
        </Card>

        {sendTest == null ? null : (
          <Card>
            <CardHeader>
              <CardTitle>Testing</CardTitle>
            </CardHeader>
            <CardSection>{sendTest}</CardSection>
          </Card>
        )}
      </Stack>
    </Stack>
  );
}
